import moment from 'moment'
import { prodRecIds } from './prodRecExtra'
import oikosApi from '../services/oikos'
import { getReg, getRegs, sendMail } from './fc'
import { currencyFormatter } from './currencyFormatter'

export const TAB_REF_DATA_LIMITE = 'prodDtLim'
export const PF_APROVADOR_DATA_LIMITE = 3
const MARKER = 'PROD_DTLIM_JSON:'

export const EMAILS_CONCLUSAO_DATA_LIMITE = [
  'diretoria.tecnica@ecossis.com.br',
  'diretoria.executiva@ecossis.com.br',
  'gerente.tecnico@ecossis.com.br',
  'gerencia.financeira@ecossis.com.br',
]

export const toYmd = (v) => {
  if (v == null || v === '') return null
  const strict = moment(v, 'YYYY-MM-DD', true)
  if (strict.isValid()) return strict.format('YYYY-MM-DD')
  const parsed = moment(v)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

export const addDaysYmd = (ymd, days) => {
  const base = toYmd(ymd)
  if (!base) return null
  return moment(base, 'YYYY-MM-DD').add(days, 'days').format('YYYY-MM-DD')
}

export const fmtDataBr = (ymd) => {
  const base = toYmd(ymd)
  return base ? moment(base, 'YYYY-MM-DD').format('DD/MM/YYYY') : '—'
}

export function payloadFromOrientacao(text) {
  const s = String(text || '')
  const i = s.lastIndexOf(MARKER)
  if (i < 0) return null
  try {
    return JSON.parse(s.slice(i + MARKER.length).trim())
  } catch (e) {
    return null
  }
}

function textoQuitacao(rec, dataBase, novaData) {
  if (Number(rec?.rec_risco) === 0) {
    return 'Pagamento já recebido. A data de quitação atualizada permanece.'
  }
  const quit = toYmd(rec?.rec_dtQuitAtual)
  const base = toYmd(dataBase)
  const nova = toYmd(novaData)
  if (!quit || !base || !nova) {
    return 'Evento ainda previsto, sem data de quitação para deslocar.'
  }
  const delta = moment(nova, 'YYYY-MM-DD').diff(moment(base, 'YYYY-MM-DD'), 'days')
  const novaQuit = addDaysYmd(quit, delta)
  const sinal = delta > 0 ? '+' : ''
  return `Quitação atualizada: ${fmtDataBr(quit)} para ${fmtDataBr(novaQuit)} (${sinal}${delta} dias).`
}

export async function criarSolicitacaoDataLimite({
  prod,
  projId,
  novaData,
  dataAnterior,
  motivo,
  usuarioCria,
}) {
  const recIds = prodRecIds(prod)
  const recId = recIds[0]
  if (!recId) {
    throw new Error('Este produto não tem evento de pagamento vinculado.')
  }
  const nova = toYmd(novaData)
  if (!nova) throw new Error('Informe a nova data.')
  if (!usuarioCria) throw new Error('Não foi possível identificar o solicitante.')
  const motivoTxt = String(motivo || '').trim()
  if (!motivoTxt) throw new Error('Informe o motivo.')
  const anterior = toYmd(dataAnterior)
  if (anterior === nova) throw new Error('A nova data é igual à data atual.')

  const rec = await getReg('rec', recId)
  if (!rec) throw new Error('Evento de pagamento não encontrado.')

  let projNome = ''
  if (projId) {
    const proj = await getReg('proj', projId)
    projNome = proj?.proj_nome || ''
  }

  const base = anterior || toYmd(rec.rec_dtNfPrev)
  const valor = currencyFormatter.format(Number(rec.rec_vlrBru) || 0)
  const quitacaoTxt = textoQuitacao(rec, base, nova)
  const resumo = [
    'Alteração da data limite de faturamento',
    `Projeto: ${projNome || projId || '—'}`,
    `Produto: ${prod.prod_desc || prod.prod_id}`,
    `Data atual: ${fmtDataBr(anterior)}`,
    `Nova data: ${fmtDataBr(nova)}`,
    `Valor do evento: ${valor}`,
    ...(recIds.length > 1 ? [`Eventos vinculados: ${recIds.join(', ')}`] : []),
    quitacaoTxt,
    `Motivo: ${motivoTxt}`,
  ].join('\n')

  const payload = {
    prod_id: Number(prod.prod_id),
    rec_id: Number(recId),
    proj_id: projId ? Number(projId) : null,
    proj_nome: projNome,
    prod_desc: prod.prod_desc || '',
    dataAnterior: anterior,
    dataBase: base,
    novaData: nova,
    motivo: motivoTxt,
    rec_vlrBru: rec.rec_vlrBru,
    rec_ep: rec.rec_ep || '',
    rec_ids: recIds,
  }
  const orientacao = `Avalie a nova data limite de faturamento. Aprove ou recuse. Na recusa, o comentário é obrigatório.\n${MARKER}${JSON.stringify(payload)}`

  const abertas = await getRegs('task', {
    task_tabRef: TAB_REF_DATA_LIMITE,
    task_tabId: Number(prod.prod_id),
    taskStatus_id: 1,
  }) || []
  for (const tarefa of abertas) {
    await oikosApi.post('/', {
      action: 'update',
      table: 'task',
      data: {
        task_id: tarefa.task_id,
        task_ativo: 0,
        task_comentario: 'Substituída por nova solicitação de alteração de data.',
      },
    })
  }

  const comum = {
    task_tabRef: TAB_REF_DATA_LIMITE,
    task_tabId: Number(prod.prod_id),
    taskStatus_id: 1,
    task_desc: resumo,
    task_orientacao: orientacao,
    task_usuarioCria: usuarioCria,
    task_prazo: moment().add(3, 'days').format('YYYY-MM-DD'),
  }
  const mae = await oikosApi.post('/', {
    action: 'insert',
    table: 'task',
    data: { ...comum, pf_id: usuarioCria },
  })
  const maeId = mae?.data?.[0]?.task_id
  if (!maeId) throw new Error('Não foi possível abrir a tarefa.')
  await oikosApi.post('/', {
    action: 'update',
    table: 'task',
    data: { task_id: maeId, task_mae: maeId },
  })
  await oikosApi.post('/', {
    action: 'insert',
    table: 'task',
    data: { ...comum, pf_id: PF_APROVADOR_DATA_LIMITE, task_mae: maeId },
  })

  try {
    const pf = await getReg('pf', PF_APROVADOR_DATA_LIMITE)
    if (pf?.pf_emailProf) {
      await sendMail(
        pf.pf_emailProf,
        `Nova tarefa: data limite de faturamento — ${prod.prod_desc || prod.prod_id}`,
        resumo.replace(/\n/g, '<br>'),
      )
    }
  } catch (e) {
    console.log('aviso ao aprovador da data limite', e)
  }
  return true
}

export async function aplicarDataLimiteAprovada(orientacao) {
  const p = payloadFromOrientacao(orientacao)
  if (!p?.prod_id || !p?.novaData || !p?.rec_id) {
    throw new Error('A tarefa não tem os dados da nova data.')
  }
  await oikosApi.post('/', {
    action: 'update',
    table: 'prod',
    data: { prod_id: p.prod_id, prod_dtLimAprov: p.novaData },
  })
  const ids = Array.isArray(p.rec_ids) && p.rec_ids.length ? p.rec_ids.map((id) => Number(id)) : [Number(p.rec_id)]
  const resultado = { quitacaoAlterada: false, novaQuitacao: null }
  const base = toYmd(p.dataBase || p.dataAnterior)
  const delta = base ? moment(p.novaData, 'YYYY-MM-DD').diff(moment(base, 'YYYY-MM-DD'), 'days') : null
  for (const id of ids) {
    if (!id) continue
    await oikosApi.post('/', {
      action: 'update',
      table: 'rec',
      data: { rec_id: id, rec_dtNfPrev: p.novaData },
    })
    const rec = await getReg('rec', id)
    if (!rec || Number(rec.rec_risco) === 0 || delta == null) continue
    const quit = toYmd(rec.rec_dtQuitAtual)
    if (!quit) continue
    const novaQuit = addDaysYmd(quit, delta)
    if (novaQuit && novaQuit !== quit) {
      await oikosApi.post('/', {
        action: 'update',
        table: 'rec',
        data: { rec_id: id, rec_dtQuitAtual: novaQuit },
      })
      if (Number(id) === Number(p.rec_id)) {
        resultado.quitacaoAlterada = true
        resultado.novaQuitacao = novaQuit
      }
    }
  }
  return resultado
}

export async function enviarEmailConclusaoDataLimite(orientacao, aprovada, comentario, resultado) {
  const p = payloadFromOrientacao(orientacao) || {}
  const desfecho = aprovada ? 'aprovada' : 'recusada'
  const quit = aprovada
    ? (resultado?.quitacaoAlterada
      ? `Data de quitação atualizada alterada para ${fmtDataBr(resultado.novaQuitacao)}.`
      : 'Data de quitação atualizada mantida.')
    : 'Nenhuma data foi alterada.'
  const html = [
    `A alteração da data limite de faturamento foi <strong>${desfecho}</strong>.`,
    `Projeto: ${p.proj_nome || '—'}`,
    `Produto: ${p.prod_desc || p.prod_id || '—'}`,
    `Data atual: ${fmtDataBr(p.dataAnterior)}`,
    `Nova data: ${fmtDataBr(p.novaData)}`,
    `Valor do evento: ${currencyFormatter.format(Number(p.rec_vlrBru) || 0)}`,
    `Motivo: ${p.motivo || '—'}`,
    `Comentário: ${comentario || '—'}`,
    quit,
  ].join('<br>')
  const subject = `Data limite de faturamento ${desfecho} — ${p.prod_desc || p.prod_id || ''}`
  for (const to of EMAILS_CONCLUSAO_DATA_LIMITE) {
    await sendMail(to, subject, html)
  }
}
